// Copyright (c) 2021 WiseTime. All rights reserved.

import { TimeGroup, Timeline, TimeRow } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { equals, uniq } from "ramda"
import { t } from "@lingui/macro"
import { Tag } from "../../generated/legebill/type/tag_pb"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { TagsCache } from "./types"
import { TagCategory } from "../../generated/legebill/type/tag_category_pb"

type TimeRowOrTimeGroup = TimeRow | TimeGroup

export const getTagIdsFromTimeline = (timeline: Timeline): string[] => {
  const tagIdsTimeRowsInRange = getTagIdsFromTimeRowsOrTimeGroups(timeline.timeRowsInRange)
  const tagIdsTimeGroupsInRange = getTagIdsFromTimeRowsOrTimeGroups(timeline.timeGroupsInRange)
  return uniq(tagIdsTimeRowsInRange.concat(tagIdsTimeGroupsInRange))
}

export const getTagIdsFromTimeRowsOrTimeGroups = (timeRowsOrTimeGroups: TimeRowOrTimeGroup[]): string[] => {
  return uniq(
    timeRowsOrTimeGroups
      .map(timeRowOrTimeGroup => timeRowOrTimeGroup.tags.map(tag => tag.id))
      .reduce((acc, tags) => acc.concat(tags), [])
  )
}

export const getTagCategoriesIdsFromTimeline = (timeline: Timeline): string[] => {
  const tagCategoriesIdsTimeRowsInRange = getTagCategoriesIdsFromTimeRowsOrTimeGroups(timeline.timeRowsInRange)
  const tagCategoriesIdsTimeGroupsInRange = getTagCategoriesIdsFromTimeRowsOrTimeGroups(timeline.timeGroupsInRange)
  return uniq(tagCategoriesIdsTimeRowsInRange.concat(tagCategoriesIdsTimeGroupsInRange))
}

export const getTagCategoriesIdsFromTimeRowsOrTimeGroups = (timeRowsOrTimeGroups: TimeRowOrTimeGroup[]): string[] => {
  return uniq(
    timeRowsOrTimeGroups
      .map(timeRowOrTimeGroup => timeRowOrTimeGroup.tagCategories.map(c => c.id))
      .reduce((acc, tagCategoriesIds) => acc.concat(tagCategoriesIds), [])
  )
}

export const getTagDescription = (tag: Tag): string => {
  let tagDesc = tag?.description
  if (tagDesc === undefined || tagDesc === "") {
    tagDesc = t({ id: "label_no_description", message: "No description." })
  }
  return tagDesc
}

export const getTagCategoryDescription = (tagCategory: TagCategory): string => {
  let tagCategoryDesc = tagCategory?.description
  if (tagCategoryDesc === undefined || tagCategoryDesc === "") {
    tagCategoryDesc = t({ id: "label_no_description", message: "No description." })
  }
  return tagCategoryDesc
}

export const hasTagChangedForUIRendering = (left: Tag | undefined, right: Tag | undefined) =>
  left?.name === right?.name
  && left?.description === right?.description
  && left?.suggestionConfig === right?.suggestionConfig
  && equals(left?.keywords, right?.keywords)
  && equals(left?.defaultTimesheetFieldValues, right?.defaultTimesheetFieldValues)

type HasDifferentTagAssigneeParams = {
  tagsList: TagRef[],
  tagsCache: TagsCache,
  currentSiteId: string | undefined,
  currentExternalId: string,
  currentUserEmail: string,
}

export const hasDifferentTagAssignee = ({
  tagsList,
  tagsCache,
  currentSiteId,
  currentExternalId,
  currentUserEmail
}: HasDifferentTagAssigneeParams): boolean => tagsList.some(t => {
  const tag: Tag | undefined = tagsCache[t.id]
  return (
    tag !== undefined
    && tag.siteId === currentSiteId
    && tag.assigneeExternalIds.length !== 0
    && [currentUserEmail, currentExternalId].every(item => !tag.assigneeExternalIds.includes(item))
  )
})
